import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addNonGovernmental,
  editNonGovernmental,
} from "services/lealIntityService";
import { CrimeReference, Investigation, NonGovernmental } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  investigation: Investigation;
  incident?: CrimeReference;
  nonGovernmental?: NonGovernmental;
  onClose: (refetch: boolean) => void;
};

const defaultNonGovernmental: NonGovernmental = {
  uu_id: 0,
  organization_name: "",
  registration_number: "",
  non_governmental_type: "",
  address: "",
  investigation: null,
};

const VitimNonGovernmentalForm: FC<Props> = ({
  isOpen,
  action,
  investigation,
  incident,
  nonGovernmental,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<NonGovernmental>(
    nonGovernmental ?? defaultNonGovernmental,
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (nonGovernmental && (investigation || incident)) {
      setFormState(nonGovernmental);
    }
  }, [nonGovernmental, investigation, incident]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.organization_name) return "Organization Name is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction =
      action === "Edit" ? editNonGovernmental : addNonGovernmental;
    submitAction({
      ...formState,
      investigation: investigation?.uu_id,
      crimereference: incident ? (incident?.uu_id as number) : null,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success",
        );
        setFormState(defaultNonGovernmental);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          Add Non-Governmental Victim
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D] w-[400px]">
          publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="organization_name"
                value={formState?.organization_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Registration Number
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="registration_number"
                value={formState?.registration_number}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Non Governmental Type
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="non_governmental_type"
                value={formState?.non_governmental_type}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Address
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="address"
                value={formState?.address}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                               text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                                       ${
                                         loading
                                           ? "opacity-50 cursor-not-allowed"
                                           : ""
                                       }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                action + " Non-Governmental Victim"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VitimNonGovernmentalForm;
